import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetPatients } from "../PatientComponents";
import { useGetOrganizations } from "../HandlingComponents";
import { toastError, toastSuccess } from "../../utils/utils";

const enrichKits = (kits, patients, organizations) =>
  (kits ?? []).map((kit) => {
    const patient = patients.find(
      (p) => Number(p.id) === Number(kit.patient_id)
    );
    const orgId = kit.org_id ?? patient?.org_id ?? null;
    const org = organizations.find((o) => Number(o.id) === Number(orgId));

    return {
      ...kit,
      org_id: orgId,
      patient_info:
        patient != null
          ? `${patient.first_name} ${patient.last_name}`
          : kit.patient_id != null
            ? `ID ${kit.patient_id}`
            : "Non associato",
      org_name: org?.name ?? "",
    };
  });

export const useGetKits = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const patientsQuery = useGetPatients();
  const organizationsQuery = useGetOrganizations();

  const isSuperAdmin = authUser()?.role === 1;

  return useQuery({
    queryKey: ["kits", patientsQuery.dataUpdatedAt ?? 0],
    queryFn: async () => {
      const res = await axiosInstance.get(`/kits`, {
        headers: { Authorization: authHeader() },
      });

      const patients = patientsQuery.data?.patients ?? [];
      const organizations = organizationsQuery.data?.organizations ?? [];
      const kits = enrichKits(res.data?.kits, patients, organizations);

      return {
        ...res.data,
        kits,
        total: res.data?.total ?? kits.length,
      };
    },
    enabled: isSuperAdmin
      ? patientsQuery.isSuccess && organizationsQuery.isSuccess
      : true,
  });
};

export const useGetKitById = (kit_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["kits", kit_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/kits/${kit_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: kit_id != null && kit_id !== undefined,
  });
};

export const useNewKit = (closeModal) => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newKit) => {
      if (authUser()?.role !== 1) {
        throw new Error("Non autorizzato a creare kit");
      }
      if (newKit.org_id == null || newKit.org_id === "") {
        toast.error("Organizzazione è un campo obbligatorio", toastError);
        throw new Error("org_id required");
      }
      const res = await axiosInstance.post(`/kits`, newKit, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["kits"]);
      queryClient.invalidateQueries(["devices"]);
      toast.success("Kit creato con successo", toastSuccess);
      closeModal?.();
    },
    onError: () => {
      toast.error("Errore nella creazione del kit", toastError);
    },
  });
};

export const usePutKitById = (kit_id) => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedKit) => {
      if (editedKit === undefined) return;
      const kitId = editedKit.id ?? kit_id;
      const role = authUser()?.role;
      const payload =
        role === 2 || role === 3
          ? { patient_id: editedKit.patient_id ?? null }
          : {
              name: editedKit.name,
              org_id: editedKit.org_id,
              patient_id: editedKit.patient_id ?? null,
            };

      if (role === 1 && (payload.org_id == null || payload.org_id === "")) {
        toast.error("Organizzazione è un campo obbligatorio", toastError);
        throw new Error("org_id required");
      }

      const res = await axiosInstance.put(`/kits/${kitId}`, payload, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["kits"]);
      queryClient.invalidateQueries(["kits", variables.id ?? kit_id]);
      if (variables.patient_id != null) {
        queryClient.invalidateQueries(["patients", variables.patient_id]);
      }
      toast.success("Kit aggiornato", toastSuccess);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.error ??
        "Errore nell'aggiornamento del kit";
      toast.error(msg, toastError);
    },
  });
};

/** PUT /kits/:kit_id/devices — `device_ids` sostituisce l'intero elenco strumenti del kit. */
export const usePutKitDevices = (kit_id) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ device_ids }) => {
      const res = await axiosInstance.put(
        `/kits/${kit_id}/devices`,
        { device_ids: (device_ids ?? []).map(Number) },
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data) => {
      const id = data?.id ?? kit_id;
      if (data?.devices != null) {
        queryClient.setQueryData(["kits", id], data);
      } else {
        queryClient.invalidateQueries(["kits", kit_id]);
      }
      queryClient.invalidateQueries(["kits"]);
      queryClient.invalidateQueries(["devices"]);
      toast.success("Strumenti del kit aggiornati", toastSuccess);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.error ??
        "Errore nell'aggiornamento degli strumenti del kit";
      toast.error(msg, toastError);
    },
  });
};

export const useDeleteKit = () => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (kit_id) => {
      if (authUser()?.role !== 1) {
        throw new Error("Non autorizzato a eliminare kit");
      }
      await axiosInstance.delete(`/kits/${kit_id}`, {
        headers: { Authorization: authHeader() },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["kits"]);
      toast.success("Kit eliminato con successo", toastSuccess);
    },
    onError: () => {
      toast.error("Errore nell'eliminazione del kit", toastError);
    },
  });
};
